/**
 * Pure helpers for Backroom fetch degradation.
 * Kept separate so poll/cache policy can be unit-tested without a React harness.
 * <!-- locus: locus-backroom -->
 */

/**
 * @param {boolean} hasSynced
 * @param {string|null|undefined} code  e.g. not_loaded | locked | network
 * @returns {'reconnecting' | 'not_loaded' | 'locked' | 'hard_error'}
 */
export function classifyFetchFailure(hasSynced, code) {
  if (code === 'locked') return 'locked';
  // Network blips and "Failed to fetch" never wipe a good stream
  if (code === 'network' || code === 'not_loaded') {
    return hasSynced ? 'reconnecting' : 'not_loaded';
  }
  return hasSynced ? 'reconnecting' : 'hard_error';
}

/** Discard out-of-order completions. */
export function shouldApplyFetchResult(requestGeneration, currentGeneration) {
  return requestGeneration === currentGeneration;
}

/** Skip interval ticks while a refresh is already running. */
export function shouldSkipIntervalTick(inFlight) {
  return Boolean(inFlight);
}

/** Block accidental double-post of the same body within a short window. */
export function isDuplicateCompose(lastBody, lastAtMs, nextBody, nowMs, windowMs = 8000) {
  if (!lastBody || !nextBody) return false;
  if (String(lastBody).trim() !== String(nextBody).trim()) return false;
  if (typeof lastAtMs !== 'number' || typeof nowMs !== 'number') return false;
  return nowMs - lastAtMs < windowMs;
}

/** Shared client + server: one presence pulse per window across cockpit surfaces. */
export const PRESENCE_THROTTLE_MS = 15 * 60 * 1000;
export const PRESENCE_THROTTLE_KEY = 'extensus.presence.throttle.v2';
export const LEGACY_PRESENCE_KEYS = ['extensus.boot.checkin.v1', 'extensus.backroom.presence.v1'];

const PRESENCE_SPAM_RE =
  /\[CHECKIN\]|presence-only|entity-boot|corpus-execute|working_on["']?\s*:\s*["']?cockpit/i;
const DAEMON_NOTE_RE = /\[NOTE\]\s*daemon command\b/i;
const SIGNAL_STAGE_RE =
  /\[(HANDOFF|ORDERS|AGREE|IDEATE|REVIEW|EXECUTE|BLOCKER|STATUS|CLAIM|RELEASE|PROPOSAL|ACK|DECIDE)\]/i;

/** Heartbeats that drowned the notebook and polluted LLM grounding. */
export function isPresenceSpam(content) {
  const t = String(content || '');
  if (!t) return false;
  if (/\[CHECKIN\]/i.test(t)) return true;
  if (DAEMON_NOTE_RE.test(t)) return true;
  if (/presence-only/i.test(t)) return true;
  if (/entity-boot/i.test(t)) return true;
  if (/corpus-execute/i.test(t) && t.length < 240) return true;
  return PRESENCE_SPAM_RE.test(t);
}

export function isSignalMessage(content) {
  const t = String(content || '');
  return SIGNAL_STAGE_RE.test(t) && !isPresenceSpam(t);
}

/**
 * Prefer real coordination rows for catch-up / grounding.
 * Falls back to non-spam recent rows if the thread has no tagged work.
 */
export function selectGroundingMessages(rows, { signalLimit = 16, fallbackLimit = 12 } = {}) {
  const list = Array.isArray(rows) ? rows : [];
  const textOf = (row) => row?.content || row?.text || '';
  const signal = list.filter((row) => isSignalMessage(textOf(row)));
  if (signal.length) return signal.slice(-signalLimit);
  return list.filter((row) => !isPresenceSpam(textOf(row))).slice(-fallbackLimit);
}

export function shouldPulsePresence(nowMs, lastTs, windowMs = PRESENCE_THROTTLE_MS, force = false) {
  if (force) return true;
  const last = Number(lastTs);
  if (!Number.isFinite(last) || last <= 0) return true;
  return nowMs - last >= windowMs;
}

/** Latest stamp from the unified key or any leftover legacy key. */
export function latestPresenceStamp(read) {
  const keys = [PRESENCE_THROTTLE_KEY, ...LEGACY_PRESENCE_KEYS];
  let max = 0;
  for (const key of keys) {
    const n = Number(read(key) || 0);
    if (Number.isFinite(n) && n > max) max = n;
  }
  return max;
}

/**
 * Short "are you better / how are you" — talk, do not dump the notebook.
 * Keep tight so "are you better at writing tests" still goes to the model.
 */
export function isSelfHealthQuery(query) {
  const t = String(query || '').trim();
  if (!t || t.length > 96) return false;
  if (/\b(backroom|notebook|handoff|claim|deploy|commit|board messages)\b/i.test(t)) return false;
  return (
    /^(?:(?:hey|hi|yo|ok|okay|so)[, ]+)?(?:are you|you)\s+(?:better|good|ok|okay|up|there|working|fixed|faster|smarter|improved|online)(?:\s+now)?\s*[?.!]?\s*$/i.test(
      t,
    ) ||
    /^(?:how are you|you good|feeling better|did (?:that|it|the (?:fix|optimi[sz]e|upgrade)) (?:work|help|land))(?:\s+now)?\s*[?.!]?\s*$/i.test(
      t,
    )
  );
}

/** Prior LIVE READ / CHECKIN walls must not be replayed as "conversation". */
export function sanitizeChatHistoryTurn(text, max = 800) {
  const s = String(text || '');
  if (!s) return '';
  if (/\[LIVE READ\]|\[CORE STATUS\]|\[CHECKIN\]|entity-boot|presence-only/i.test(s) || isPresenceSpam(s)) {
    const first = s.split('\n').filter(Boolean).slice(0, 2).join(' — ');
    return `${first.slice(0, 180)} …[truncated dump]`;
  }
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

/** Stable names for CHECKIN. Aliases group; never mint grok-4.7 / grok-4.6 / cli-grok / X as live agents. */
export const CANONICAL_AGENT_ALIASES = {
  grok: 'Grok',
  'grok-4.7': 'Grok',
  'grok-4.6': 'Grok',
  'cli-grok': 'Grok',
  codex: 'Codex',
  antigravity: 'Antigravity',
  extensus: 'EXTENSUS',
  jarvis: 'JARVIS',
  builder: 'Builder',
  chatgpt: 'ChatGPT',
  tony: 'Tony',
};

export function canonicalAgentId(name) {
  const raw = String(name || '').trim();
  if (!raw) return '';
  const mapped = CANONICAL_AGENT_ALIASES[raw.toLowerCase()];
  return mapped || raw;
}

export const PRESENCE_LIVE_MS = 15 * 60 * 1000;
export const PRESENCE_OFFLINE_MS = 60 * 60 * 1000;

export function presenceAgeMs(lastSeenAt, nowMs = Date.now()) {
  const t = Date.parse(String(lastSeenAt || '').replace(' ', 'T'));
  if (!Number.isFinite(t)) return Number.POSITIVE_INFINITY;
  return nowMs - t;
}

/** LIVE <=15m, STALE 15-60m, OFFLINE >60m. Presence never renews a file claim. */
export function classifyPresence(lastSeenAt, nowMs = Date.now()) {
  const age = presenceAgeMs(lastSeenAt, nowMs);
  if (age <= PRESENCE_LIVE_MS) return 'LIVE';
  if (age <= PRESENCE_OFFLINE_MS) return 'STALE';
  return 'OFFLINE';
}

/**
 * Newest exact / canonical agent match.
 * Lane is not identity: EXTENSUS pulsing lane=JARVIS must not paint the JARVIS chip live.
 * Does not substring-match (GROK must not steal grok-4.6 leftovers).
 */
export function pickLivePresence(presence, laneId) {
  const id = String(laneId || '').toUpperCase();
  const rows = Array.isArray(presence) ? presence : [];
  const matches = rows.filter((p) => {
    const agent = String(p.agent || '').toUpperCase();
    const canon = canonicalAgentId(p.agent).toUpperCase();
    return agent === id || canon === id;
  });
  matches.sort((a, b) => presenceAgeMs(a.last_seen_at) - presenceAgeMs(b.last_seen_at));
  return matches[0] || null;
}

export function extractCoordId(content) {
  const t = String(content || '');
  const named = t.match(/coord-[a-z0-9-]+/i);
  if (named) return named[0].toLowerCase();
  const reply = t.match(/reply_to=#(\d{3,})/i);
  if (reply) return `#${reply[1]}`;
  const hash = t.match(/#(\d{3,})/);
  if (hash) return `#${hash[1]}`;
  return null;
}

export function latestByStage(rows, stage) {
  const tag = String(stage || '').toUpperCase();
  const list = Array.isArray(rows) ? rows : [];
  const hit = list.filter((row) => new RegExp(`^\\[${tag}\\]`, 'i').test(String(row?.content || row?.text || '')));
  return hit.at(-1) || null;
}

/** PROPOSAL rows with no ACK that cites their coord-* id or #message-id. */
export function findOpenProposals(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const textOf = (row) => String(row?.content || row?.text || '');
  const proposals = list.filter((row) => /^\[PROPOSAL\]/i.test(textOf(row)));
  const ackHay = list
    .filter((row) => /^\[ACK\]/i.test(textOf(row)))
    .map((row) => textOf(row).toLowerCase())
    .join('\n');
  return proposals
    .filter((row) => {
      const body = textOf(row);
      const coord = extractCoordId(body);
      const ids = [];
      if (coord) ids.push(coord.toLowerCase());
      if (row?.id != null) ids.push(`#${row.id}`);
      if (!ids.length) return true;
      return !ids.some((id) => ackHay.includes(id.toLowerCase()));
    })
    .slice(-5);
}

export function snippet(text, max = 140) {
  return String(text || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, max);
}
